/*!
 * Copyright (c) Boa-Franc.
 * P2534-98 : recherche des factures par numéro de facture, de commande ou de PO.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { boaSearchInvoicesAsync } from './DataService/DataActionExtension.g';
import { IBoaSearchInvoicesResponse } from './DataService/DataServiceEntities.g';

/**
 * Entrée de l'action de recherche de factures.
 */
export class BoaSearchInvoicesInput implements IActionInput {
    public searchTerm: string;

    public customerAccountNumber: string;

    public businessPartnerId: string;

    public maxResults: number;

    public constructor(searchTerm: string, customerAccountNumber: string, businessPartnerId: string, maxResults: number) {
        this.searchTerm = searchTerm;
        this.customerAccountNumber = customerAccountNumber;
        this.businessPartnerId = businessPartnerId;
        this.maxResults = maxResults;
    }

    /**
     * Aucune mise en cache : le résultat est propre à un client et à un terme, et les
     * factures évoluent au fil des paiements. La clé reste portée par le compte et le
     * terme afin qu'un cache activé par erreur ne serve jamais le résultat d'une autre
     * recherche.
     */
    public getCacheKey = () => `BoaSearchInvoicesInput-${this.customerAccountNumber}-${this.searchTerm}`;

    public getCacheObjectType = () => 'BoaSearchInvoicesInput';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (
    args: ICreateActionContext,
    searchTerm: string,
    customerAccountNumber: string,
    businessPartnerId: string,
    maxResults: number
): IActionInput => {
    return new BoaSearchInvoicesInput(searchTerm, customerAccountNumber, businessPartnerId, maxResults);
};

/**
 * Interroge le CSU. Une erreur est journalisée puis relancée : le module affiche son
 * propre message et ne réessaie jamais automatiquement.
 */
export async function BoaSearchInvoices(input: BoaSearchInvoicesInput, ctx: IActionContext): Promise<IBoaSearchInvoicesResponse> {
    return boaSearchInvoicesAsync(
        { callerContext: ctx },
        input.searchTerm,
        input.customerAccountNumber,
        input.businessPartnerId,
        input.maxResults
    )
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[boa-search-invoices] Unable to search invoices');
            throw new Error('[boa-search-invoices] Unable to search invoices');
        });
}

export default createObservableDataAction({
    action: <IAction<IBoaSearchInvoicesResponse>>BoaSearchInvoices,
    id: 'BoaSearchInvoices',
    input: createInput
});
